import React, { useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Webhook, CheckCircle, XCircle, Copy } from 'lucide-react';
import { toast } from 'sonner';

const AdminWebhookConfig = () => {
  const [webhookUrl, setWebhookUrl] = useState('');

  React.useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    setWebhookUrl(`${backendUrl}/api/webhooks/razorpay`);
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const webhookEvents = [
    { event: 'payment.captured', description: 'Payment is captured successfully', required: true },
    { event: 'payment.failed', description: 'Payment failed', required: false },
    { event: 'payment.authorized', description: 'Payment is authorized', required: false },
    { event: 'order.paid', description: 'Order is paid', required: false }
  ];

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="font-playfair text-4xl font-bold mb-2">Webhook Configuration</h1>
        <p className="font-manrope text-sm text-gray-600">Configure Razorpay webhooks for automatic payment updates</p>
      </div>

      {/* Webhook URL */}
      <div className="border border-gray-200 p-6 mb-6 bg-white">
        <div className="flex items-center gap-2 mb-4">
          <Webhook className="w-5 h-5" />
          <h2 className="font-playfair text-2xl font-bold">Webhook Endpoint</h2>
        </div>
        
        <p className="font-manrope text-sm text-gray-600 mb-4">
          Use this URL in your Razorpay Dashboard webhook configuration
        </p>
        
        <div className="flex gap-2">
          <input
            type="text"
            value={webhookUrl}
            readOnly
            className="flex-1 border border-gray-200 px-4 py-3 font-mono text-sm bg-gray-50"
          />
          <button
            onClick={() => copyToClipboard(webhookUrl)}
            className="px-4 py-3 border border-gray-200 hover:border-black transition-all duration-300"
            title="Copy URL"
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Setup Steps */}
      <div className="border border-gray-200 p-6 mb-6 bg-white">
        <h2 className="font-playfair text-2xl font-bold mb-6">Setup Instructions</h2>
        
        <div className="space-y-6">
          <div className="flex gap-4">
            <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-mono text-sm flex-shrink-0">
              1
            </div>
            <div>
              <h3 className="font-manrope font-semibold mb-2">Login to Razorpay Dashboard</h3>
              <p className="font-manrope text-sm text-gray-600">
                Go to <a href="https://dashboard.razorpay.com/" target="_blank" rel="noopener noreferrer" className="text-black underline">dashboard.razorpay.com</a> and switch to Live Mode
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-mono text-sm flex-shrink-0">
              2
            </div>
            <div>
              <h3 className="font-manrope font-semibold mb-2">Navigate to Webhooks</h3>
              <p className="font-manrope text-sm text-gray-600">
                Settings → Webhooks → Add New Webhook
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-mono text-sm flex-shrink-0">
              3
            </div>
            <div>
              <h3 className="font-manrope font-semibold mb-2">Configure Webhook</h3>
              <p className="font-manrope text-sm text-gray-600 mb-3">
                Paste the webhook URL above and select events
              </p>
              <div className="bg-gray-50 border border-gray-200 p-4">
                <div className="font-mono text-xs break-all mb-2">{webhookUrl}</div>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-mono text-sm flex-shrink-0">
              4
            </div>
            <div>
              <h3 className="font-manrope font-semibold mb-2">Copy Webhook Secret</h3>
              <p className="font-manrope text-sm text-gray-600 mb-3">
                After creating webhook, copy the secret (starts with whsec_) and add to backend .env:
              </p>
              <div className="bg-gray-900 text-green-400 p-4 font-mono text-xs">
                RAZORPAY_WEBHOOK_SECRET="whsec_xxxxxxxxxxxxx"
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-mono text-sm flex-shrink-0">
              5
            </div>
            <div>
              <h3 className="font-manrope font-semibold mb-2">Test Webhook</h3>
              <p className="font-manrope text-sm text-gray-600">
                Use the "Test Webhook" button in Razorpay dashboard to send a test event
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Events to Subscribe */}
      <div className="border border-gray-200 p-6 mb-6 bg-white">
        <h2 className="font-playfair text-2xl font-bold mb-6">Events to Subscribe</h2>
        
        <div className="space-y-3">
          {webhookEvents.map((item) => (
            <div key={item.event} className="flex items-start gap-3 p-3 border border-gray-200">
              {item.required ? (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <div className="w-5 h-5 border border-gray-300 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-sm font-semibold">{item.event}</span>
                  {item.required && (
                    <span className="px-2 py-0.5 bg-black text-white font-manrope text-xs uppercase tracking-widest">
                      Required
                    </span>
                  )}
                </div>
                <p className="font-manrope text-sm text-gray-600">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Security Notes */}
      <div className="border border-black p-6 bg-gray-50">
        <h2 className="font-playfair text-2xl font-bold mb-4">Security Notes</h2>
        
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="font-manrope text-sm">
              All webhooks are signature-verified using HMAC SHA256
            </p>
          </div>
          
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="font-manrope text-sm">
              Webhook endpoint requires HTTPS in production
            </p>
          </div>
          
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="font-manrope text-sm">
              Payment status only updated after successful signature verification
            </p>
          </div>
          
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="font-manrope text-sm">
              Duplicate webhook events are automatically handled
            </p>
          </div>
        </div>
      </div>

      {/* Documentation Link */}
      <div className="mt-6 text-center">
        <p className="font-manrope text-sm text-gray-600 mb-2">
          For detailed documentation, see:
        </p>
        <a
          href="/RAZORPAY_WEBHOOK_SETUP.md"
          target="_blank"
          className="font-mono text-sm text-black underline"
        >
          /RAZORPAY_WEBHOOK_SETUP.md
        </a>
      </div>
    </AdminLayout>
  );
};

export default AdminWebhookConfig;
